<template>
   <div class="interpretation-container">
      <div class="interpretation-section">
        <h3 class="section-title">AUC Interpretation</h3>
        <div v-if="!store.estimatedAUC">
          please enter the estimated AUC !
        </div>
        <div v-else class="interpretation-content">
          <p class="interpret-item" v-html="compareToAUC"></p>
          <p class="interpret-item" v-html="AUCcompareToInterval"></p>
          <p class="interpret-item" v-html="AUCCompareToToxicity"></p>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
    import { computed } from "vue";
    import { useTestStore } from "@/stores/testStore";

    // Initialize the store
    const store = useTestStore();

    const parse = v => {
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };

    const compareToAUC = computed(() => {
        if (!store.estimatedAUC || !store.ecoff || !store.currentAtbDataRow["AUC/CMI"]) {
            return '<span class="status-neutral">⚠️ Please provide all required values (AUC and organism).</span>';
        }

        const targetRatio = parseInt(store.currentAtbDataRow["AUC/CMI"], 10);
        const currentRatio = store.estimatedAUC / store.ecoff;

        if (currentRatio > targetRatio) {
            return `<span class="status-success">✓ <strong>AUC/CMI ratio</strong> : <strong>${currentRatio.toFixed(1)}</strong> is <strong>higher</strong> than target ratio : <strong>${targetRatio}</strong>. This suggests adequate exposure.</span>`;
        } else {
            return `<span class="status-warning">⚠️ <strong>AUC/CMI ratio</strong> : <strong>${currentRatio.toFixed(1)}</strong> is <strong>lower</strong> than target ratio : <strong>${targetRatio}</strong>. </span>`;
        }
    })

    const AUCminMax = computed(() => {
        if (!store.currentAtbDataRow) return { min: undefined, max: undefined };
        return {
            min: parse(store.currentAtbDataRow["AUCmin usuelle"]),
            max: parse(store.currentAtbDataRow["AUCmax usuelle"]),
        };
    });

    const AUCcompareToInterval = computed(() => {
    if (AUCminMax.value.min == null || AUCminMax.value.max == null) {
        return '<span class="status-neutral">⚠️ No data available for therapeutic dosage range.</span>';
    }
    if (store.estimatedAUC < AUCminMax.value.min) {
        return `<span class="status-warning">⚠️ <strong>Estimated 24-hours AUC </strong> (${store.estimatedAUC}mg.h/L) is <strong>below</strong>  the therapeutic dosage range (${AUCminMax.value.min} - ${AUCminMax.value.max}mg/L).</span>`;
    } else if (store.estimatedAUC > AUCminMax.value.max) {
        return `<span class="status-warning">⚠️ <strong>Estimated 24-hours AUC </strong> (${store.estimatedAUC}mg.h/L) is <strong>over</strong>  the therapeutic dosage range (${AUCminMax.value.min} - ${AUCminMax.value.max}mg/L).</span>`;
    } else {
        return `<span class="status-success">✅ <strong>Estimated 24-hours AUC </strong> (${store.estimatedAUC}mg.h/L) is <strong>within</strong> the therapeutic dosage range (${AUCminMax.value.min} - ${AUCminMax.value.max}mg/L).</span>`;
    }
    });

    const aucToxicityThreshold = computed(() => {
    return {
        value: store.currentAtbDataRow[`AUC toxicité`],
        type: store.currentAtbDataRow[`type AUC toxicité`]
    };
    });

    const AUCCompareToToxicity = computed(() => {
    if (!store.currentAtbDataRow || aucToxicityThreshold.value == null) {
        return `<strong>Toxicity</strong> data not available for AUC related toxicity.`;
    }

    if (store.estimatedAUC > aucToxicityThreshold.value.value) {
        return `<strong>⚠️ Estimated 24-hours AUC </strong> (${store.estimatedAUC}mg.h/L) is <strong>higher</strong> than the <strong>toxicity threshold</strong> (${aucToxicityThreshold.value.value}mg/L)${
        aucToxicityThreshold.value.type
            ? `, potential <strong>${aucToxicityThreshold.value.type}</strong> toxicity`
            : ``
        }`;
    } else {
        return `<strong>✅ Estimated 24-hours AUC</strong> (${store.estimatedAUC}mg.h/L)  is <strong>below</strong> the <strong>toxicity threshold</strong> (${aucToxicityThreshold.value.value} mg/L)`;
    }
    });

    
  </script>
  
  <style scoped>
  .interpretation-container {
    margin-top: 20px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .interpretation-section {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    width: 100%;
    min-width: 600px;
    box-sizing: border-box;
  }

  .section-title {
    color: #2c3e50;
    margin: 0 0 16px 0;
    font-size: 1.25rem;
    font-weight: 600;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 8px;
  }

  .interpretation-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .interpret-item {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    text-align: left;
  }

  .interpret-item strong {
    color: #2c3e50;
  }

  .status-success {
    color: #198754;
    display: block;
    padding: 8px;
    border-left: 4px solid #198754;
  }

  .status-warning {
    color: #ffc107;
    display: block;
    padding: 8px;
    border-left: 4px solid #ffc107;
  }

  .status-error {
    color: #dc3545;
    display: block;
    padding: 8px;
    border-left: 4px solid #dc3545;
  }

  .status-neutral {
    color: #6c757d;
    display: block;
    padding: 8px;
    border-left: 4px solid #6c757d;
  }
  </style> 